import Api from 'entities/helpers/api';
import EverTrue from 'app';

/**
 * Fetches proposal field values using Elasticsearch facets with field pattern
 *
 * @param {string} fieldName - The field to query ('primary_proposal_unit' or 'additional_proposal_category')
 * @param {string} query - Optional search query to filter values (default: '')
 * @param {number} limit - Maximum number of results (default: 500)
 * @returns {Promise<Array<string>>} Array of field value strings
 */
export const fetchProposalFieldValues = async (fieldName, query = '', limit = 500) => {
  try {
    const facetQuery = {
      facet: [
        {
          field: `${fieldName}.untouched`,
          ...(query &&
            query.trim() && {
              filter: {
                [fieldName]: { wildcard: `*${query.trim()}*` },
              },
            }),
        },
      ],
    };

    const response = await Api.SEARCH.PROPOSAL_STATS.post({
      data: JSON.stringify(facetQuery),
      params: { limit: 0 }, // Only need facet data, not documents
    });

    const facets = response?.facets?.[`${fieldName}.untouched`] || [];

    const values = facets
      .filter((facet) => facet.value && facet.value.trim() && facet.value.trim().length > 1)
      .slice(0, limit)
      .map((facet) => facet.value);
    return values;
  } catch (error) {
    EverTrue.Alert.error(`Error fetching ${fieldName} values:`, error);
    return [];
  }
};

/**
 * Convenience functions for specific fields
 */

/**
 * Fetches all unique primary proposal unit values
 * @param {string} query - Optional search query
 * @param {number} limit - Optional result limit
 * @returns {Promise<Array<string>>} Array of unit strings
 */
export const fetchPrimaryProposalUnits = async (query = '', limit = 500) => {
  return fetchProposalFieldValues('primary_proposal_unit', query, limit);
};

/**
 * Fetches all unique additional proposal category values
 * @param {string} query - Optional search query
 * @param {number} limit - Optional result limit
 * @returns {Promise<Array<string>>} Array of category strings
 */
export const fetchAdditionalProposalCategories = async (query = '', limit = 500) => {
  return fetchProposalFieldValues('additional_proposal_category', query, limit);
};
